import { Age, Colors, Gender } from 'utils/constants/aiImageGeneration';

import createReducer, { ActionCreatorHelper } from 'store/createReducer';
import { ImageInfoResponse } from 'store/models/createBanner';

type IsAiImageGenerationModalVisible = boolean;
type AiImageQueueId = number | null;
type IsGenerationError = boolean;

export type SelectorsValueType = Age | Gender | Colors;

export interface AiImageQueuePosition {
    queuePosition: number;
    waitingTimeMs: number;
}

export interface Selectors {
    age: Age;
    gender: Gender;
    clothes: Colors;
    background: Colors;
}

interface AiGenerationState {
    isAiImageGenerationModalVisible: IsAiImageGenerationModalVisible;
    aiImageQueueId: AiImageQueueId;
    generatedImages: ImageInfoResponse[];
    selectors: Selectors | null;
    generationPosition: AiImageQueuePosition | null;
    isGenerationError: IsGenerationError;
    remainingAiImageGenerations: number;
    savedImagesIds: number[];
    imagesLoaded: Record<number, boolean>;
}

const SET_AI_IMAGE_GENERATION_SHOW = 'SET_AI_IMAGE_GENERATION_SHOW';
const SET_AI_IMAGE_QUEUE_ID = 'SET_AI_GENERATION_QUEUE_ID';
const SET_GENERATED_IMAGE = 'SET_GENERATED_IMAGE';
const SET_SELECTOR = 'SET_SELECTOR';
const SET_GENERATION_POSITION = 'SET_GENERATION_POSITION';
const RESET_AI_IMAGE_GENERATION = 'RESET_AI_IMAGE_GENERATION';
const SET_IS_GENERATION_ERROR = 'SET_IS_GENERATION_ERROR';
const SET_REMAINING_AI_IMAGE_GENERATIONS = 'SET_REMAINING_AI_IMAGE_GENERATIONS';
const SET_SAVED_IMAGE_ID = 'SET_SAVED_IMAGE_ID';
const SET_IMAGE_LOADED = 'SET_IMAGE_LOADED';

interface PayloadTypes {
    [SET_AI_IMAGE_GENERATION_SHOW]: IsAiImageGenerationModalVisible;
    [SET_AI_IMAGE_QUEUE_ID]: AiImageQueueId;
    [SET_GENERATED_IMAGE]: ImageInfoResponse;
    [SET_SELECTOR]: {
        selector: keyof Selectors;
        value: Selectors[keyof Selectors];
    };
    [SET_GENERATION_POSITION]: AiImageQueuePosition | null;
    [RESET_AI_IMAGE_GENERATION]: null;
    [SET_IS_GENERATION_ERROR]: IsGenerationError;
    [SET_REMAINING_AI_IMAGE_GENERATIONS]: number;
    [SET_SAVED_IMAGE_ID]: number;
    [SET_IMAGE_LOADED]: number;
}

const ActionCreator = ActionCreatorHelper<PayloadTypes>();

export const setIsAiGenerationModalVisible = ActionCreator(SET_AI_IMAGE_GENERATION_SHOW);
export const setGenerationQueueId = ActionCreator(SET_AI_IMAGE_QUEUE_ID);
export const setGeneratedImage = ActionCreator(SET_GENERATED_IMAGE);
export const setSelector = ActionCreator(SET_SELECTOR);
export const setGenerationPosition = ActionCreator(SET_GENERATION_POSITION);
export const resetAiImageGeneration = ActionCreator(RESET_AI_IMAGE_GENERATION);
export const setIsGenerationError = ActionCreator(SET_IS_GENERATION_ERROR);
export const setRemainingAiImageGenerations = ActionCreator(SET_REMAINING_AI_IMAGE_GENERATIONS);
export const setSavedImageId = ActionCreator(SET_SAVED_IMAGE_ID);
export const setImageLoaded = ActionCreator(SET_IMAGE_LOADED);

export const INITIAL_SELECTORS: Selectors = {
    age: Age.Any,
    gender: Gender.Any,
    clothes: Colors.Random,
    background: Colors.Random,
};

const INITIAL_STATE: AiGenerationState = {
    isAiImageGenerationModalVisible: false,
    aiImageQueueId: null,
    generatedImages: [],
    selectors: INITIAL_SELECTORS,
    generationPosition: null,
    isGenerationError: false,
    remainingAiImageGenerations: 0,
    savedImagesIds: [],
    imagesLoaded: {},
};

export default createReducer<AiGenerationState, PayloadTypes>(INITIAL_STATE, {
    [SET_AI_IMAGE_GENERATION_SHOW]: (state, { payload }) => ({
        ...state,
        isAiImageGenerationModalVisible: payload,
    }),
    [SET_AI_IMAGE_QUEUE_ID]: (state, { payload }) => ({
        ...state,
        aiImageQueueId: payload,
    }),
    [SET_GENERATED_IMAGE]: (state, { payload }) => ({
        ...state,
        generatedImages: [payload, ...state.generatedImages],
    }),
    [SET_SELECTOR]: (state, { payload }) => ({
        ...state,
        selectors: {
            ...(state.selectors ?? INITIAL_SELECTORS),
            [payload.selector]: payload.value,
        },
    }),
    [SET_GENERATION_POSITION]: (state, { payload }) => ({
        ...state,
        generationPosition: payload,
    }),
    [RESET_AI_IMAGE_GENERATION]: () => ({
        ...INITIAL_STATE,
    }),
    [SET_IS_GENERATION_ERROR]: (state, { payload }) => ({
        ...state,
        isGenerationError: payload,
    }),
    [SET_REMAINING_AI_IMAGE_GENERATIONS]: (state, { payload }) => ({
        ...state,
        remainingAiImageGenerations: payload,
    }),
    [SET_SAVED_IMAGE_ID]: (state, { payload }) => ({
        ...state,
        savedImagesIds: [...state.savedImagesIds, payload],
    }),
    [SET_IMAGE_LOADED]: (state, { payload }) => ({
        ...state,
        imagesLoaded: {
            ...state.imagesLoaded,
            [payload]: true,
        },
    }),
});
